import { DocumentUtilities } from '@pwc/pwc-deals-web-common';
import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import _uniq from 'lodash/uniq';
import _get from 'lodash/get';
import { postIngestStructuredDocument } from '../services/structuredAnalysisService';
import { clearAllProjectData } from '../actions/clearAllProjectData';

export const initialState = {
  ingestedDocuments: [],
  loadingInProcess: [],
  successfullyIngestedTime: {},
  errorMessages: {},
};

const structuredAnalysisSlice = createSlice({
  name: 'structuredAnalysis',
  initialState,
  reducers: {
    clearIngestError: (state, action) => ({
      ...state,
      loadingInProcess: state.loadingInProcess.filter(
        (item) => item !== action.payload.code
      ),
      errorMessages: {},
    }),
    ingestionBegan: (state, action) => {
      const errorMessages = cloneDeep(state.errorMessages);
      action.payload.forEach((x) => {
        delete errorMessages[x.code];
      });

      return {
        ...state,
        loadingInProcess: [
          ...state.loadingInProcess,
          ...action.payload.map((x) => x.code),
        ],
        errorMessages,
      };
    },
    ingestionSucceeded: (state, action) => {
      const successfullyIngestedTime =
        cloneDeep(state.successfullyIngestedTime) || {};
      action.payload.forEach((x) => {
        successfullyIngestedTime[x.code] = new Date();
      });
      const errorMessages = cloneDeep(state.errorMessages);
      action.payload.forEach((x) => {
        delete errorMessages[x.code];
      });

      return {
        ...state,
        ingestedDocuments: _uniq([
          ...state.ingestedDocuments,
          ...action.payload.map((x) => x.code),
        ]),
        loadingInProcess: [
          ...state.loadingInProcess.filter((item) => {
            return action.payload.find((x) => x.code === item) === undefined;
          }),
        ],
        successfullyIngestedTime,
        errorMessages,
      };
    },
    ingestionFailed: (state, action) => {
      const errorMessages = cloneDeep(state.errorMessages);
      action.payload.codes.forEach((x) => {
        errorMessages[x.code] = action.payload.error;
      });
      return {
        ...state,
        loadingInProcess: [
          ...state.loadingInProcess.filter((item) => {
            return (
              action.payload.codes.find((x) => x.code === item) === undefined
            );
          }),
        ],
        errorMessages,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearAllProjectData, () => initialState);
  },
});
export default structuredAnalysisSlice.reducer;

// Actions
export const {
  clearIngestError,
  ingestionBegan,
  ingestionSucceeded,
  ingestionFailed,
  setActiveVersion,
} = structuredAnalysisSlice.actions;

// Selectors
export const getIngestedDocuments = (state) =>
  state.structuredAnalysis.ingestedDocuments;
export const getLoadingInProcess = (state) =>
  state.structuredAnalysis.loadingInProcess;
export const getStructuredAnalysisHasError = (state) =>
  Object.keys(state.structuredAnalysis.errorMessages).length > 0;
export const getStructuredAnalysisErrors = (state) =>
  state.structuredAnalysis.errorMessages;
export const getStructuredAnalysisError = (state) =>
  state.structuredAnalysis.errorMessages[
    Object.keys(state.structuredAnalysis.errorMessages)[0]
  ];
export const getSuccessfullyIngestedDocumentTimes = (state) =>
  state.structuredAnalysis.successfullyIngestedTime;

// Thunks
export const IngestionThunk2 =
  (projectId, documentCodes) => async (dispatch) => {
    try {
      documentCodes.forEach((documentCode) =>
        dispatch(ingestionBegan([{ code: documentCode }]))
      );
      const response = await postIngestStructuredDocument(
        projectId,
        documentCodes
      );
      documentCodes.forEach((documentCode) =>
        dispatch(ingestionSucceeded([{ code: documentCode }]))
      );
      return response;
    } catch (error) {
      const errorMessage = _get(
        error,
        'response.data.errors[0].apiErrorMessage',
        'An error occurred'
      );
      documentCodes.forEach((documentCode) => {
        const errorObject = {
          error: errorMessage,
          codes: [{ code: documentCode }],
        };
        dispatch(ingestionFailed(errorObject));
      });
    }
    return null;
  };
export const IngestionThunk = (projectId, documentCode) => async (dispatch) => {
  try {
    dispatch(ingestionBegan([{ code: documentCode }]));
    await postIngestStructuredDocument(projectId, [documentCode]);
    dispatch(ingestionSucceeded([{ code: documentCode }]));
  } catch (error) {
    const errorMessage = _get(
      error,
      'response.data.errors[0].apiErrorMessage',
      'An error occurred'
    );
    const errorObject = {
      error: errorMessage,
      codes: [{ code: documentCode }],
    };
    dispatch(ingestionFailed(errorObject));
  }
};

export const ReingestDocumentWithDependencies =
  (projectId, documentCode) => async (dispatch) => {
    const documentWithDependencies =
      DocumentUtilities.getDocumentWithDependencies(documentCode);
    const documentsForRefresh = [
      documentWithDependencies.document,
      ...documentWithDependencies.dependencies,
    ];
    const codes = documentsForRefresh.map((code) => ({ code }));
    try {
      dispatch(ingestionBegan(codes));
      await postIngestStructuredDocument(projectId, documentsForRefresh);
      dispatch(ingestionSucceeded(codes));
    } catch (error) {
      const errorMessage = _get(
        error,
        'response.data.errors[0].apiErrorMessage',
        'An error occurred'
      );
      const errorObject = { error: errorMessage, codes };
      dispatch(ingestionFailed(errorObject));
    }
  };
